import { useEffect } from 'react';
import { 
  FileText, 
  BookOpen, 
  Calculator, 
  FileSpreadsheet, 
  Download, 
  Upload, 
  Edit, 
  CheckCircle, 
  Clock,
  Users
} from 'lucide-react';
import { Card } from '../ui/Card';
import { useActivities, ActivityEvent } from '../../hooks/useActivityTracking';

// Données d'activités initiales pour peupler le dashboard
const generateInitialActivities = (): Omit<ActivityEvent, 'id' | 'timestamp'>[] => {
  return [
    {
      type: 'journal',
      action: 'Nouvelle écriture ajoutée',
      description: 'Facture client XYZ SARL - 1,180,000 CDF',
      user: 'Admin',
      status: 'success',
      module: 'Journal'
    },
    {
      type: 'validation',
      action: 'Validation en lot ADHA',
      description: '12 écritures validées par l\'agent comptable',
      user: 'ADHA AI',
      status: 'success',
      module: 'Agent Comptable'
    },
    {
      type: 'export',
      action: 'Export PDF généré',
      description: 'Journal des écritures - Juin 2025',
      user: 'Admin',
      status: 'success',
      module: 'Export'
    },
    {
      type: 'ledger',
      action: 'Grand livre consulté',
      description: 'Compte 411000 - Clients',
      user: 'Comptable',
      status: 'success',
      module: 'Grand Livre'
    },
    {
      type: 'declaration',
      action: 'Déclaration TVA préparée',
      description: 'Déclaration mensuelle - Mai 2025',
      user: 'Admin',
      status: 'warning',
      module: 'Déclarations'
    },
    {
      type: 'calculation',
      action: 'Calcul des ratios financiers',
      description: 'Mise à jour des indicateurs de performance',
      user: 'Système',
      status: 'success',
      module: 'Tableau de bord'
    },
    {
      type: 'import',
      action: 'Import de données',
      description: 'Relevé bancaire - 45 transactions importées',
      user: 'Admin',
      status: 'success',
      module: 'Import'
    },
    {
      type: 'journal',
      action: 'Écriture modifiée',
      description: 'Correction facture fournisseur ABC SA',
      user: 'Comptable',
      status: 'success',
      module: 'Journal'
    }
  ];
};

const getActivityIcon = (type: ActivityEvent['type']) => {
  const iconClass = "h-4 w-4 flex-shrink-0";
  
  switch (type) {
    case 'journal':
      return <FileText className={iconClass} />;
    case 'ledger':
      return <BookOpen className={iconClass} />;
    case 'declaration':
      return <FileSpreadsheet className={iconClass} />;
    case 'export':
      return <Download className={iconClass} />;
    case 'import':
      return <Upload className={iconClass} />;
    case 'validation':
      return <CheckCircle className={iconClass} />;
    case 'calculation':
      return <Calculator className={iconClass} />;
    case 'user_action':
      return <Users className={iconClass} />;
    default:
      return <Edit className={iconClass} />;
  }
};

const getStatusColor = (status: ActivityEvent['status']) => {
  switch (status) {
    case 'success':
      return 'text-green-600 bg-green-50 dark:bg-green-900/20';
    case 'warning':
      return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20';
    case 'error':
      return 'text-red-600 bg-red-50 dark:bg-red-900/20';
    case 'pending':
      return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20';
    default:
      return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20';
  }
};

const getTimeAgo = (timestamp: Date) => {
  const now = new Date();
  const diffInMs = now.getTime() - timestamp.getTime();
  const diffInMin = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  
  if (diffInMin < 60) {
    return `Il y a ${diffInMin} min`;
  } else if (diffInHours < 24) {
    return `Il y a ${diffInHours}h`;
  } else {
    return timestamp.toLocaleDateString('fr-FR');
  }
};

export function RecentActivitiesCard() {
  const { activities, addActivity, getRecentActivities } = useActivities();
  
  // Initialiser avec des données d'exemple si pas d'activités
  useEffect(() => {
    if (activities.length === 0) {
      const initialActivities = generateInitialActivities();
      initialActivities.forEach(activity => addActivity(activity));
    }
  }, [activities.length, addActivity]);

  const recentActivities = getRecentActivities(8);

  return (
    <Card title="Activités Récentes" className="h-[500px]">
      <div className="h-[420px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
        <div className="space-y-3 pr-2">
          {recentActivities.map((activity: ActivityEvent) => (
            <div
              key={activity.id}
              className="flex items-start space-x-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors duration-150"
            >
              {/* Icône et statut */}
              <div className={`p-2 rounded-full ${getStatusColor(activity.status)}`}>
                {getActivityIcon(activity.type)}
              </div>
              
              {/* Contenu */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {activity.action}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {activity.description}
                    </p>
                  </div>
                  
                  {/* Badge du module */}
                  <span className="ml-2 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full whitespace-nowrap">
                    {activity.module}
                  </span>
                </div>
                
                {/* Footer avec utilisateur et timestamp */}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                    <Users className="h-3 w-3" />
                    <span>{activity.user}</span>
                  </div>
                  <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400">
                    <Clock className="h-3 w-3" />
                    <span>{getTimeAgo(activity.timestamp)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Footer avec lien vers l'historique complet */}
      <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
        <button className="w-full text-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium transition-colors duration-150">
          Voir l'historique complet →
        </button>
      </div>
    </Card>
  );
}
